import { useRef, useEffect, useState, useImperativeHandle, forwardRef, useMemo } from 'react';
import { EditorState, Compartment } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';
import { oneDark } from '@codemirror/theme-one-dark';
import { foldGutter, foldKeymap } from '@codemirror/language';
import { linter, lintGutter } from '@codemirror/lint';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { yCollab } from 'y-codemirror.next';
import api from '../api/client';
import { buildPythonEditorExtensions, DEFAULT_EDITOR_SETTINGS } from '../editor/codemirrorConfig';
import {
  abcdef,
  abyss,
  androidstudio,
  andromeda,
  atomone,
  aura,
  bbedit,
  bespin,
  copilot,
  darcula,
  dracula,
  duotoneDark,
  eclipse,
  githubDark,
  gruvboxDark,
  kimbie,
  material,
  monokai,
  monokaiDimmed,
  noctisLilac,
  nord,
  okaidia,
  quietlight,
  red,
  solarizedDark,
  sublime,
  tokyoNight,
  tokyoNightDay,
  tokyoNightStorm,
  tomorrowNightBlue,
  vscodeDark,
} from '@uiw/codemirror-themes-all';
import { useTheme } from '../context/ThemeContext';
import { whenYjsSynced } from '../utils/whenYjsSynced';

const THEME_OPTIONS = [
  // alias для старых значений
  { id: 'dark', label: 'Тёмная (One Dark)', ext: oneDark },
  // популярные темы
  { id: 'oneDark', label: 'One Dark', ext: oneDark },
  { id: 'tokyoNight', label: 'Tokyo Night', ext: tokyoNight },
  { id: 'tokyoNightStorm', label: 'Tokyo Night Storm', ext: tokyoNightStorm },
  { id: 'tokyoNightDay', label: 'Tokyo Night Day', ext: tokyoNightDay },
  { id: 'dracula', label: 'Dracula', ext: dracula },
  { id: 'darcula', label: 'Darcula', ext: darcula },
  { id: 'monokai', label: 'Monokai', ext: monokai },
  { id: 'monokaiDimmed', label: 'Monokai Dimmed', ext: monokaiDimmed },
  { id: 'nord', label: 'Nord', ext: nord },
  { id: 'gruvboxDark', label: 'Gruvbox Dark', ext: gruvboxDark },
  { id: 'material', label: 'Material', ext: material },
  { id: 'vscodeDark', label: 'VS Code Dark', ext: vscodeDark },
  { id: 'githubDark', label: 'GitHub Dark', ext: githubDark },
  { id: 'solarizedDark', label: 'Solarized Dark', ext: solarizedDark },
  // ещё несколько «фановых» тем
  { id: 'abcdef', label: 'Abcdef', ext: abcdef },
  { id: 'abyss', label: 'Abyss', ext: abyss },
  { id: 'androidstudio', label: 'Android Studio', ext: androidstudio },
  { id: 'andromeda', label: 'Andromeda', ext: andromeda },
  { id: 'atomone', label: 'Atom One', ext: atomone },
  { id: 'aura', label: 'Aura', ext: aura },
  { id: 'bbedit', label: 'BBEdit', ext: bbedit },
  { id: 'bespin', label: 'Bespin', ext: bespin },
  { id: 'copilot', label: 'Copilot', ext: copilot },
  { id: 'duotoneDark', label: 'Duotone Dark', ext: duotoneDark },
  { id: 'eclipse', label: 'Eclipse', ext: eclipse },
  { id: 'kimbie', label: 'Kimbie', ext: kimbie },
  { id: 'noctisLilac', label: 'Noctis Lilac', ext: noctisLilac },
  { id: 'okaidia', label: 'Okaidia', ext: okaidia },
  { id: 'quietlight', label: 'Quiet Light', ext: quietlight },
  { id: 'red', label: 'Red', ext: red },
  { id: 'sublime', label: 'Sublime', ext: sublime },
  { id: 'tomorrowNightBlue', label: 'Tomorrow Night Blue', ext: tomorrowNightBlue },
];

export { THEME_OPTIONS };

const CollaborativeEditor = forwardRef(function CollaborativeEditor({
  sessionId,
  onContentChange,
  theme = 'dark',
  onThemeChange,
  onYdocReady,
  onRunRequest,
  onStatusChange,
  editorSettings,
  styleLintEnabled: styleLintEnabledProp,
  onFormattingChange,
  reconnectNonce = 0,
}, ref) {
  const editorRef = useRef(null);
  const { theme: siteTheme } = useTheme();
  const viewRef = useRef(null);
  const providerRef = useRef(null);
  const docRef = useRef(null);
  const ytextRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [userCount, setUserCount] = useState(0);
  const [formatting, setFormatting] = useState(false);
  const styleLintEnabled = styleLintEnabledProp ?? true;
  /** Стабильный ключ: при смене настроек редактора пересобираем CodeMirror (раньше учитывались только sessionId и theme). */
  const editorSettingsKey = useMemo(
    () => JSON.stringify({ ...DEFAULT_EDITOR_SETTINGS, ...(editorSettings && typeof editorSettings === 'object' ? editorSettings : {}) }),
    [editorSettings],
  );
  const onContentChangeRef = useRef(onContentChange);
  const onThemeChangeRef = useRef(onThemeChange);
  const onYdocReadyRef = useRef(onYdocReady);
  const onRunRequestRef = useRef(onRunRequest);
  const onStatusChangeRef = useRef(onStatusChange);
  const lintErrorsRef = useRef([]);
  const lintTimeoutRef = useRef(null);
  const styleLintEnabledRef = useRef(true);
  const lintCompartmentRef = useRef(new Compartment());
  const ytextObserverRef = useRef(null);
  onContentChangeRef.current = onContentChange;
  onThemeChangeRef.current = onThemeChange;
  onYdocReadyRef.current = onYdocReady;
  onRunRequestRef.current = onRunRequest;
  onStatusChangeRef.current = onStatusChange;

  useEffect(() => {
    if (typeof onStatusChangeRef.current === 'function') {
      onStatusChangeRef.current({ connected, userCount });
    }
  }, [connected, userCount]);

  useEffect(() => {
    styleLintEnabledRef.current = styleLintEnabled;
    if (viewRef.current) {
      const exts = styleLintEnabled
        ? [lintGutter(), pythonStyleLinter()]
        : [];
      viewRef.current.dispatch({
        effects: lintCompartmentRef.current.reconfigure(exts),
      });
    }
  }, [styleLintEnabled]);

  function pythonStyleLinter() {
    return linter((view) => {
      if (!styleLintEnabledRef.current) return [];
      const doc = view.state.doc;
      return lintErrorsRef.current.map((err) => {
        const line = Math.max(1, err.line || 1);
        const column = Math.max(1, err.column || 1);
        const lineInfo = doc.line(line);
        const from = lineInfo.from + (column - 1);
        const to = from;
        return {
          from,
          to,
          severity: 'warning',
          message: err.message || 'Стилевая ошибка',
          source: 'pycodestyle',
        };
      });
    });
  }

  function triggerLint(code) {
    if (!styleLintEnabledRef.current) return;
    if (lintTimeoutRef.current) {
      clearTimeout(lintTimeoutRef.current);
    }
    lintTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await api.post('/judge/python/lint/', { code });
        const errors = Array.isArray(res.data?.errors) ? res.data.errors : [];
        lintErrorsRef.current = errors;
        if (viewRef.current) {
          const exts = styleLintEnabledRef.current
            ? [lintGutter(), pythonStyleLinter()]
            : [];
          viewRef.current.dispatch({
            effects: lintCompartmentRef.current.reconfigure(exts),
          });
        }
      } catch (e) {
        // тихо игнорируем ошибку линтера, чтобы не мешать редактированию
        console.error('Python lint failed', e);
      }
    }, 500);
  }

  async function handleFormatClick() {
    if (!ytextRef.current || formatting) return;
    const ytext = ytextRef.current;
    const ydoc = docRef.current;
    const view = viewRef.current;
    const currentCode = ytext.toString();
    const scroller = editorRef.current?.querySelector('.cm-scroller');
    const scrollTop = scroller ? scroller.scrollTop : 0;
    const docLen = view?.state.doc.length ?? 1;
    const cursorRatio = view && docLen > 0 ? view.state.selection.main.head / docLen : 0;
    setFormatting(true);
    if (typeof onFormattingChange === 'function') onFormattingChange(true);
    try {
      const res = await api.post('/judge/python/format/', { code: currentCode });
      const formatted = res.data?.formatted_code;
      if (typeof formatted === 'string' && ydoc) {
        ydoc.transact(() => {
          ytext.delete(0, ytext.length);
          ytext.insert(0, formatted);
        });
        triggerLint(formatted);
        requestAnimationFrame(() => {
          const v = viewRef.current;
          const el = editorRef.current?.querySelector('.cm-scroller');
          if (el) el.scrollTop = scrollTop;
          if (v && formatted.length > 0) {
            const pos = Math.min(Math.floor(cursorRatio * formatted.length), formatted.length);
            v.dispatch({ selection: { anchor: pos, head: pos } });
          }
        });
      }
    } catch (e) {
      console.error('Python format failed', e);
    } finally {
      setFormatting(false);
      if (typeof onFormattingChange === 'function') onFormattingChange(false);
    }
  }

  useImperativeHandle(ref, () => ({
    format: handleFormatClick,
    reconnect: () => {
      const provider = providerRef.current;
      if (!provider) return;
      try {
        provider.disconnect();
      } catch {
        // ignore
      }
      try {
        provider.connect();
      } catch {
        // ignore
      }
    },
  }), []);

  useEffect(() => {
    if (!reconnectNonce) return;
    const provider = providerRef.current;
    if (!provider) return;
    try {
      provider.disconnect();
    } catch {
      // ignore
    }
    try {
      provider.connect();
    } catch {
      // ignore
    }
  }, [reconnectNonce]);

  useEffect(() => {
    if (!editorRef.current || !sessionId) return;

    const ydoc = new Y.Doc();
    docRef.current = ydoc;

    const isSecure = window.location.protocol === 'https:';
    const wsScheme = isSecure ? 'wss' : 'ws';
    // y-websocket формирует URL как serverUrl + '/' + room → ws://host/ws/collaborative/<sessionId>
    const serverUrl = `${wsScheme}://${window.location.host}/ws/collaborative`;
    const provider = new WebsocketProvider(serverUrl, sessionId, ydoc);
    providerRef.current = provider;

    const ytext = ydoc.getText('codemirror');
    ytextRef.current = ytext;

    provider.on('status', (event) => {
      setConnected(event.status === 'connected');
    });

    provider.awareness.on('change', () => {
      setUserCount(provider.awareness.getStates().size);
    });

    const observer = () => {
      const content = ytext.toString();
      if (onContentChangeRef.current) {
        onContentChangeRef.current?.(content);
      }
      triggerLint(content);
    };
    ytextObserverRef.current = observer;
    ytext.observe(observer);
    const unsubSync = whenYjsSynced(provider, () => {
      if (onYdocReadyRef.current) {
        onYdocReadyRef.current(ydoc, { awareness: provider.awareness });
      }
    });

    const runKeymap = onRunRequestRef.current
      ? keymap.of([
          { key: 'Ctrl-Enter', run: () => { onRunRequestRef.current?.(); return true; } },
          { key: 'Mod-Enter', run: () => { onRunRequestRef.current?.(); return true; } },
        ])
      : null;

    const selectedTheme =
      THEME_OPTIONS.find((t) => t.id === (siteTheme === 'dark' ? 'dark' : 'quietlight')) ||
      THEME_OPTIONS.find((t) => t.id === 'oneDark') ||
      THEME_OPTIONS[0];
    const themeExt = selectedTheme.ext;
    const lintExtensions = styleLintEnabledRef.current
      ? [lintGutter(), pythonStyleLinter()]
      : [];

    const extensions = [
      ...buildPythonEditorExtensions({
        settings: editorSettings ?? DEFAULT_EDITOR_SETTINGS,
        themeExt,
        height: '100%',
        readOnly: false,
        includeRunKeymap: Boolean(runKeymap),
        runKeymap,
      }),
      foldGutter(),
      ...(Array.isArray(foldKeymap) ? [keymap.of(foldKeymap)] : []),
      yCollab(ytext, provider.awareness),
      lintCompartmentRef.current.of(lintExtensions),
    ];

    const state = EditorState.create({
      doc: ytext.toString(),
      extensions,
    });

    const view = new EditorView({
      state,
      parent: editorRef.current,
    });

    viewRef.current = view;

    return () => {
      unsubSync();
      if (onYdocReadyRef.current) onYdocReadyRef.current(null);
      if (ytextObserverRef.current && ytextRef.current) {
        ytextRef.current.unobserve(ytextObserverRef.current);
      }
      if (lintTimeoutRef.current) {
        clearTimeout(lintTimeoutRef.current);
      }
      view.destroy();
      provider.destroy();
      ydoc.destroy();
      viewRef.current = null;
      providerRef.current = null;
      docRef.current = null;
      ytextRef.current = null;
      ytextObserverRef.current = null;
    };
  }, [sessionId, siteTheme, editorSettingsKey]);

  return (
    <div className="collaborative-editor">
      <div className="code-editor-wrapper" ref={editorRef}></div>
    </div>
  );
});

export default CollaborativeEditor;
